import { defineComponent as _defineComponent } from 'vue'

// eslint-disable-next-line @typescript-eslint/no-var-requires
const __default__ = require('@nuxtjs/composition-api').defineComponent({
  name: 'PricingPlans',
  middleware({ store, i18n, redirect }: any) {
    const usersSecondaryLocation = store.getters.loggedInUser?.location?.secondary;
    if (
      store.getters.isAuthenticated &&
      usersSecondaryLocation &&
      usersSecondaryLocation !== i18n.locale
    ) {
      redirect('/' + usersSecondaryLocation + '/plans/');
    }
  },
  jsonld() {
    // @ts-ignore
    return this.$jsonSchemaPage(this.title, this.$route.path);
  },
  head() {
    return {
      // @ts-ignore
      title: this.title,
      meta: [
        // @ts-ignore
        { hid: 'title', name: 'title', content: this.title },
        {
          hid: 'description',
          name: 'description',
          // @ts-ignore
          content: this.description,
        },
      ],
      // @ts-ignore
      link: this.$hrefLangsPage(this.$route),
    };
  },
});

import {
  computed,
  onBeforeMount,
  onMounted,
  ref,
  useContext,
  useRoute,
  useStore,
} from '@nuxtjs/composition-api';
import { plans as plansDefault } from '~/data/plans';
import { Faqs, plansFaqs } from '~/data/plans_faqs';
import ReviewsLink from '~/components/plans/ReviewsLink.vue';
import TestimonialsSection from '~/components/plans/TestimonialsSection.vue';
import PlanCard from '~/components/plans/PlanCard.vue';
import PlansDetails from '~/components/plans/PlansDetails.vue';
import TogglePlans from '~/components/plans/TogglePlans.vue';
import PlanHeader from '~/components/plans/PlanHeader.vue';
import { useUser } from '~/composables/useUser';
import { State } from '~/store';
import FaqSection from '~/components/plans/FaqSection.vue';
import tAccordion from '~/components/atoms/t-accordion.vue';
import { useLocale } from '~/composables/useLocale';
import SchoolPlanCallout from '~/components/plans/SchoolPlanCallout.vue';

type AnyPlans = (typeof plansDefault)['us' | 'gb' | 'au'];

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props, { expose }) {

const store = useStore<State>();
const route = useRoute();
const { user } = useUser();
const { $isFlagOn, $axios, $getExperimentVariant, $mzAnalyticsApi } = useContext();

const filterPlans = ref('annually');

const locale = useLocale();
const allPlans = computed(() => plansDefault[locale.value]);
const location = computed(() => store.state.location);

const filterPlansByPeriod = (plans: AnyPlans) => {
  return plans.filter(plan => !plan.period || plan.period === 'annually');
};

const australianVersion = computed(() => locale.value === 'au');

const filterPlansByFlag = (plans: AnyPlans) =>
  plans.filter(plan =>
    locale.value === 'us'
      ? ($getExperimentVariant('us-new-plan') === 'show-accessible' &&
          plan.code !== 'plus_monthly') ||
        ($getExperimentVariant('us-new-plan') === 'control' && plan.code !== 'plus_lite') ||
        ($getExperimentVariant('us-new-plan') === 'hide-monthly' && plan.code !== 'plus_lite') ||
        (!$getExperimentVariant('us-new-plan') && plan.code !== 'plus_lite')
      : ($isFlagOn('school-plan-discount') &&
          plan.code !== 'essential_annual' &&
          australianVersion.value) ||
        ($isFlagOn('school-plan-discount') && !australianVersion.value) ||
        (!$isFlagOn('school-plan-discount') && plan.code !== 'school_plan')
  );

const plans = ref(filterPlansByPeriod(filterPlansByFlag(plansDefault[locale.value])));
const mobilePlansTableEnabled = ref(false);
const faqsByRegion = computed(() =>
  filterFaqsByVariant(plansFaqs.filter((faq: Faqs) => faq.region.includes(locale.value)))
);
const filterFaqsByVariant = (faqs: Faqs[]) => {
  return faqs.filter(faq =>
    locale.value === 'us'
      ? ($getExperimentVariant('us-new-plan') === 'show-accessible' &&
          (faq.experiment === true || !faq.experiment)) ||
        (($getExperimentVariant('us-new-plan') === 'control' ||
          $getExperimentVariant('us-new-plan') === 'hide-monthly' ||
          !$getExperimentVariant('us-new-plan')) &&
          faq.experiment === false)
      : !faq.experiment
  );
};
const hasMonthlyPlans = computed(() => allPlans.value.some(plan => plan.period === 'monthly'));
const useWideCards = computed(() => plans.value.length <= 2);

const togglePlanPeriod = (value: string): void => {
  filterPlans.value = value;
  plans.value = filterPlansByFlag(plansDefault[locale.value]).filter(
    plan => !plan.period || plan.period === value
  );
};

let count = 0;
const trackComparePlanClick = (): void => {
  if (count % 2 === 0) {
    $mzAnalyticsApi({
      event: 'click',
      name: 'upgrade.compare_plan_details',
      productArea: 'profile',
    });
  }
  count++;
};
onBeforeMount(() => {
  const referral = route.value.query?.rsCode || route.value.query?.referral;
  if (referral && $isFlagOn('saasquatch-integration')) {
    store.dispatch('setReferralCode', referral);
  }
});

onMounted(async () => {
  $mzAnalyticsApi({
    event: 'pageView',
  });
  if (window.innerWidth < 600) {
    mobilePlansTableEnabled.value = true;
  }
  try {
    if (!user.value && process.browser) {
      const geoLocation = await $axios.$get('/public/v1/geoip');
      if (geoLocation?.name !== location && geoLocation?.secondary !== locale.value) {
        await store.dispatch('toggleLocationSwitchModal', { show: true });
        await store.dispatch('updateGeolocation', geoLocation);
      }
    }
  } catch (err) {
    console.log('GeoIP not found, error: ' + err);
  }
});

const title = ref("Expand what's possible in your classroom");
const description = ref(
  'Join thousands of other teachers and gain access to a library of unique, high quality teaching resources to brighten your classroom and engage your students'
);

expose({
  title,
  description,
});

return { __sfc: true,store, route, user, $isFlagOn, $axios, $getExperimentVariant, $mzAnalyticsApi, filterPlans, locale, allPlans, location, filterPlansByPeriod, australianVersion, filterPlansByFlag, plans, mobilePlansTableEnabled, faqsByRegion, filterFaqsByVariant, hasMonthlyPlans, useWideCards, togglePlanPeriod, count, trackComparePlanClick, title, description, ReviewsLink, TestimonialsSection, PlanCard, PlansDetails, TogglePlans, PlanHeader, FaqSection, tAccordion, SchoolPlanCallout }
}

})